// src/components/doctor/SlideOverDrawer.jsx
//
// Generic right-edge slide-over panel. Built for DoctorTriageView.jsx's
// Documents/Notes icons (20 Jul 2026 UX pass — pulled these two out of the
// main tab bar since they're reference material about the PATIENT, not
// interpretations of THIS triage result, and were competing for tab-bar
// space with the actual clinical tabs). Generic shell, not tied to either
// use case — pass whatever content as children.

import React, { useEffect, useRef } from 'react';
import Icon from '../Icon';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function SlideOverDrawer({ open, title, icon, onClose, children }) {
  const panelRef = useRef(null);
  // Callers pass a fresh onClose every render; keep it in a ref so the effect below
  // runs once per open/close and does not steal focus back on every re-render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Modal behaviour: focus moves in when it opens, Tab stays inside, Escape closes,
  // and focus goes back to whatever opened it.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const items = () => Array.from(panelRef.current?.querySelectorAll(FOCUSABLE) || []).filter(el => !el.disabled);
    (items()[0] || panelRef.current)?.focus();
    function handleKey(e) {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const list = items();
      if (list.length === 0) { e.preventDefault(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const inside = panelRef.current?.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(13,30,53,0.35)',
        }}
      />

      {/* Panel — full width on mobile, capped on desktop */}
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          outline: 'none',
          position: 'relative', height: '100%',
          width: 'min(420px, 100vw)',
          background: 'var(--bg-subtle)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          animation: 'rpt-drawer-in 0.2s ease-out',
        }}
      >
        <style>{`
          @keyframes rpt-drawer-in {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
          }
        `}</style>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {icon && <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--brand-accent)' }}><Icon name={icon} size={18} /></span>}
            <span style={{
              fontFamily: 'var(--rpt-ff)', fontSize: 17,
              color: 'var(--text-primary)', fontWeight: 500,
            }}>
              {title}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none', background: 'none', fontSize: 18,
              cursor: 'pointer', color: 'var(--rpt-ink-3)', padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
